export function createFukushizaiDisplaySyncService(deps) {
    const {
        entrydetailRef,
        selectedFukushizaiContextRef,
        fukushizaiRepresentativeRowRef,
        FUKUSHIZAI_BASE_PANEL_TYPE,
        FUKUSHIZAI_CONTEXT_DISPLAY_PANEL_TYPES,
        isEmpty,
        isEnabled,
        DicVal,
        canonicalPanelType,
        normalizeColorCd,
        normalizeFukushizaiContext,
        readFukushizaiContextFromRow,
        resolveFukushizaiDisplaySourceColumnId,
        getDetailRowsByPanelType,
        readFukushizaiDisplayExplicitBaseRowId,
        createFukushizaiGroupDetailRow,
        ensureFukushizaiDetailRowsForMutation,
        sameItemGroupColor,
        fukushizaiColorCodesForContext,
        contextColorCodesFromRows,
        readFukushizaiMasterCodeNameFromValue,
        extractMasterCodeFromDisplayValue,
        getFukushizaiDisplayFieldIdByPanelType,
        clearFukushizaiMasterNameFields,
        formatFukushizaiMasterDisplayValue,
        clearFukushizaiDisplayProjectionFields,
        isFukushizaiVerticalMasterSourceColumnId,
        debugFukushizaiSyncLog
    } = deps;

    // 表示行から detail 同期対象の rowid / sourceColumn / context を解決します。
    function resolveFukushizaiDisplaySyncTarget(panelInfo, row, rowIndex = -1, contextOverride = null) {
        const type = typeof panelInfo === "string"
            ? canonicalPanelType(panelInfo)
            : canonicalPanelType(DicVal(panelInfo, "type", panelInfo));
        const selected = normalizeFukushizaiContext(contextOverride || selectedFukushizaiContextRef.current);
        const rowContext = readFukushizaiContextFromRow(row);
        const context = normalizeFukushizaiContext({
            itemCd: rowContext.itemCd || selected.itemCd,
            groupNo: rowContext.groupNo || selected.groupNo,
            colorCd: rowContext.colorCd || selected.colorCd
        });

        const explicitBaseRowId = readFukushizaiDisplayExplicitBaseRowId(row);
        const hasExactBaseRow = explicitBaseRowId !== ""
            && getDetailRowsByPanelType(entrydetailRef.current, FUKUSHIZAI_BASE_PANEL_TYPE)
                .some((detailRow) => String(DicVal(detailRow, "rowid", "") ?? "").trim() === explicitBaseRowId);
        const baseRowId = hasExactBaseRow ? explicitBaseRowId : "";
        const sourceInfo = resolveFukushizaiDisplaySourceColumnId(type, row, rowIndex);
        const matchInfo = {
            ...row,
            rowid: baseRowId !== "" ? String(DicVal(row, "rowid", "") ?? "").trim() : "",
            __fukushizaiBaseRowId: baseRowId,
            __komonofudaBaseRowId: baseRowId,
            __brandNameBaseRowId: baseRowId,
            __otherNameBaseRowId: baseRowId,
            __otherFukuzaiBaseRowId: baseRowId,
            SSDZ_ITMCITEMCD: context.itemCd,
            SSDZ_FUKUSIZAICOLORGRP: context.groupNo,
            SSDZ_COLORCD: context.colorCd
        };

        return {
            baseRowId,
            sourceColumnId: sourceInfo.sourceColumnId,
            rowIndex: sourceInfo.rowIndex,
            fallbackSourceColumnIdUsed: sourceInfo.fallbackSourceColumnIdUsed,
            context,
            matchInfo,
            fallbackUsed: baseRowId === ""
        };
    }

    // 表示 row から保存値候補を読む際の優先キーを吸収します。
    function readFukushizaiDisplayRawValue(panelType, row, sourceColumnId, changedField = "", rawValue = undefined) {
        if (rawValue !== undefined) return rawValue;

        const type = canonicalPanelType(panelType);
        const displayFieldId = getFukushizaiDisplayFieldIdByPanelType(type);
        const keys = [
            changedField,
            displayFieldId,
            sourceColumnId,
            `${sourceColumnId}_cd`,
            `${sourceColumnId}_CD`,
            `${displayFieldId}_cd`,
            `${displayFieldId}_CD`
        ].filter((key) => !isEmpty(key));

        for (let i = 0; i < keys.length; i++) {
            const value = DicVal(row, keys[i], undefined);
            if (value !== undefined && value !== null) return value;
        }
        return "";
    }

    // 表示値を D_SSDZ に保存する code 形式へ正規化します。
    function normalizeFukushizaiDisplaySaveValue(panelType, value, sourceColumnId) {
        const type = canonicalPanelType(panelType);
        if (type === "114") return String(value ?? "");
        if (isEmpty(value) || typeof value !== "object") return extractMasterCodeFromDisplayValue(value);
        return readFukushizaiMasterCodeNameFromValue(sourceColumnId, value).code;
    }

    // 現在 context に対して反映対象になる色コード一覧を決めます。
    function resolveFukushizaiDisplayTargetColors(context) {
        const normalized = normalizeFukushizaiContext(context);
        if (normalized.itemCd === "" || normalized.groupNo === "") return [];

        const colors = fukushizaiColorCodesForContext(normalized.itemCd, normalized.groupNo);
        if (colors.length !== 0) return colors;

        const detailColors = contextColorCodesFromRows(
            getDetailRowsByPanelType(entrydetailRef.current, FUKUSHIZAI_BASE_PANEL_TYPE),
            normalized.itemCd,
            normalized.groupNo,
            FUKUSHIZAI_BASE_PANEL_TYPE
        );
        if (detailColors.length !== 0) return detailColors;

        return normalized.colorCd !== "" ? [normalized.colorCd] : [];
    }

    // 現時点では詳細ログは止めておき、呼び出し点だけ残します。
    function logFukushizaiDisplaySyncFlat(payload) {
        return;
    }

    // 現時点では詳細ログは止めておき、呼び出し点だけ残します。
    function debugFukushizaiDisplayFlush(panelType, row, syncTarget, result) {
        return;
    }

    // 114/115/116/117 の表示セル変更を D_SSDZ 明細行へ反映します。
    function syncFukushizaiDisplayCellToDetail(args = {}) {
        const sourceArgs = isEmpty(args) || typeof args !== "object" ? {} : args;
        const panelInfo = DicVal(sourceArgs, "panelInfo", null);
        const panelType = canonicalPanelType(DicVal(sourceArgs, "panelType", DicVal(panelInfo, "type", "")));
        const row = DicVal(sourceArgs, "row", null);
        const rowIndexArg = DicVal(sourceArgs, "rowIndex", -1);
        const changedField = String(DicVal(sourceArgs, "changedField", "") ?? "").trim();
        const rawValue = Object.prototype.hasOwnProperty.call(sourceArgs, "rawValue")
            ? DicVal(sourceArgs, "rawValue", undefined)
            : undefined;
        const timing = String(DicVal(sourceArgs, "timing", "immediate") ?? "immediate").trim() || "immediate";
        const syncTarget = resolveFukushizaiDisplaySyncTarget(panelInfo || panelType, row, rowIndexArg, DicVal(sourceArgs, "context", null));
        const baseLog = {
            timing,
            panelType,
            rowIndex: syncTarget.rowIndex,
            changedField,
            sourceColumnId: syncTarget.sourceColumnId,
            selectedItem: syncTarget.context.itemCd,
            selectedGroup: syncTarget.context.groupNo,
            fallbackUsed: syncTarget.fallbackUsed || syncTarget.fallbackSourceColumnIdUsed
        };

        // 114/115/116/117 以外はこの同期対象ではありません。
        if (!FUKUSHIZAI_CONTEXT_DISPLAY_PANEL_TYPES.includes(panelType)) {
            logFukushizaiDisplaySyncFlat({ ...baseLog, updatedCount: 0, skipReason: "unsupported-panel" });
            return { changed: false, index: -1, row: null, context: syncTarget.context, updatedCount: 0, skipReason: "unsupported-panel" };
        }
        if (syncTarget.sourceColumnId === "") {
            logFukushizaiDisplaySyncFlat({ ...baseLog, updatedCount: 0, skipReason: "missing-source-column" });
            return { changed: false, index: -1, row: null, context: syncTarget.context, updatedCount: 0, skipReason: "missing-source-column" };
        }
        if (syncTarget.context.itemCd === "" || syncTarget.context.groupNo === "") {
            logFukushizaiDisplaySyncFlat({ ...baseLog, updatedCount: 0, skipReason: "missing-item-group" });
            return { changed: false, index: -1, row: null, context: syncTarget.context, updatedCount: 0, skipReason: "missing-item-group" };
        }

        const targetColors = resolveFukushizaiDisplayTargetColors(syncTarget.context);
        if (targetColors.length === 0) {
            logFukushizaiDisplaySyncFlat({ ...baseLog, targetColors, updatedCount: 0, skipReason: "missing-target-colors" });
            return { changed: false, index: -1, row: null, context: syncTarget.context, updatedCount: 0, skipReason: "missing-target-colors" };
        }

        const displayValue = readFukushizaiDisplayRawValue(panelType, row, syncTarget.sourceColumnId, changedField, rawValue);
        const saveValue = normalizeFukushizaiDisplaySaveValue(panelType, displayValue, syncTarget.sourceColumnId);

        // 表示 row 側にも code 変更を即時反映して、見た目と保存値を揃えます。
        if (isFukushizaiVerticalMasterSourceColumnId(syncTarget.sourceColumnId) && !isEmpty(row) && typeof row === "object") {
            const displayCodeName = readFukushizaiMasterCodeNameFromValue(syncTarget.sourceColumnId, displayValue);
            const displayName = displayCodeName.code === saveValue ? displayCodeName.name : "";
            const displayField = getFukushizaiDisplayFieldIdByPanelType(panelType);
            clearFukushizaiMasterNameFields(row, syncTarget.sourceColumnId, {
                timing,
                panelType,
                oldCode: extractMasterCodeFromDisplayValue(DicVal(row, syncTarget.sourceColumnId, "")),
                newCode: saveValue,
                reason: "display-row-code-changed"
            });
            row[syncTarget.sourceColumnId] = saveValue;
            if (displayField !== "") row[displayField] = formatFukushizaiMasterDisplayValue(saveValue, displayName);
        }

        // deferred 同期では空値を保存側へ流さないようにします。
        if (timing !== "immediate" && String(saveValue ?? "").trim() === "") {
            logFukushizaiDisplaySyncFlat({
                ...baseLog,
                displayValue: typeof displayValue === "object"
                    ? readFukushizaiMasterCodeNameFromValue(syncTarget.sourceColumnId, displayValue)
                    : String(displayValue ?? ""),
                saveValue,
                targetColors,
                updatedCount: 0,
                skipReason: "non-immediate-empty"
            });
            return { changed: false, index: -1, row: null, context: syncTarget.context, updatedCount: 0, skipReason: "non-immediate-empty" };
        }

        const detailRows = ensureFukushizaiDetailRowsForMutation();
        let changed = false;
        let updatedCount = 0;
        let firstIndex = -1;
        let firstRow = null;

        targetColors.forEach((colorCd) => {
            const normalizedColor = normalizeColorCd(colorCd);
            if (normalizedColor === "") return;

            let index = detailRows.findIndex((detailRow) =>
                sameItemGroupColor(detailRow, syncTarget.context.itemCd, syncTarget.context.groupNo, normalizedColor)
            );
            if (index < 0) {
                const created = createFukushizaiGroupDetailRow(syncTarget.context.itemCd, syncTarget.context.groupNo, normalizedColor, null, false);
                if (isEmpty(created)) return;
                index = detailRows.length - 1;
            }

            const currentRow = detailRows[index];
            if (isEmpty(currentRow)) return;

            const nextRow = { ...currentRow };
            if (nextRow[syncTarget.sourceColumnId] !== saveValue) {
                if (isFukushizaiVerticalMasterSourceColumnId(syncTarget.sourceColumnId)) {
                    clearFukushizaiMasterNameFields(nextRow, syncTarget.sourceColumnId, {
                        timing,
                        panelType,
                        oldCode: extractMasterCodeFromDisplayValue(nextRow[syncTarget.sourceColumnId]),
                        newCode: extractMasterCodeFromDisplayValue(saveValue),
                        reason: "code-changed"
                    });
                }
                nextRow[syncTarget.sourceColumnId] = saveValue;
                clearFukushizaiDisplayProjectionFields(nextRow);
                detailRows[index] = nextRow;
                changed = true;
            }

            updatedCount++;
            if (firstIndex < 0) {
                firstIndex = index;
                firstRow = detailRows[index];
            }
        });

        if (firstIndex >= 0 && !isEmpty(firstRow)) {
            fukushizaiRepresentativeRowRef.current = {
                rowid: String(DicVal(firstRow, "rowid", firstIndex) ?? firstIndex),
                index: firstIndex
            };
        }

        const result = {
            changed,
            index: firstIndex,
            row: firstRow,
            context: isEmpty(firstRow) ? syncTarget.context : readFukushizaiContextFromRow(firstRow),
            updatedCount,
            skipReason: ""
        };

        logFukushizaiDisplaySyncFlat({
            ...baseLog,
            displayValue: typeof displayValue === "object"
                ? readFukushizaiMasterCodeNameFromValue(syncTarget.sourceColumnId, displayValue)
                : String(displayValue ?? ""),
            saveValue,
            targetColors,
            updatedCount,
            skipReason: ""
        });
        debugFukushizaiDisplayFlush(panelType, row, { ...syncTarget, displayValue, saveValue }, result);
        return result;
    }

    // 表示 row 1 行ぶんの同期入口を panel 単位で包みます。
    function applyFukushizaiDisplayRowToDetail(panelInfo, row, rowIndex = -1, options = {}) {
        const type = canonicalPanelType(DicVal(panelInfo, "type", ""));
        if (!FUKUSHIZAI_CONTEXT_DISPLAY_PANEL_TYPES.includes(type)) {
            return { changed: false, index: -1, row: null, context: normalizeFukushizaiContext(row) };
        }
        if (isEmpty(row) || isEnabled(DicVal(row, "__fukushizaiCopyPreview", false))) {
            return { changed: false, index: -1, row: null, context: normalizeFukushizaiContext(row) };
        }

        return syncFukushizaiDisplayCellToDetail({
            panelInfo,
            panelType: type,
            row,
            rowIndex,
            changedField: DicVal(options, "changedField", ""),
            rawValue: DicVal(options, "rawValue", undefined),
            context: DicVal(options, "context", null),
            timing: DicVal(options, "timing", "immediate")
        });
    }

    return {
        resolveFukushizaiDisplaySyncTarget,
        readFukushizaiDisplayRawValue,
        normalizeFukushizaiDisplaySaveValue,
        resolveFukushizaiDisplayTargetColors,
        syncFukushizaiDisplayCellToDetail,
        applyFukushizaiDisplayRowToDetail
    };
}
